import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

export type UserRole = 'admin' | 'editor' | 'cliente' | 'guest' | 'rsvp' | null;

export function useUserRole() {
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserRole = async () => {
      console.log("👤 [USER_ROLE] Buscando role do usuário...");
      try {
        const { data: { user } } = await supabase.auth.getUser();
        console.log("👤 [USER_ROLE] Usuário obtido:", user ? `ID: ${user.id}` : "Nenhum usuário");
        
        if (!user) {
          setRole(null);
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('❌ [USER_ROLE] Erro ao buscar role:', error);
          console.log('👤 [USER_ROLE] Definindo role como guest devido ao erro');
          setRole('guest');
        } else {
          const userRole = data?.role as UserRole || 'guest';
          console.log('✅ [USER_ROLE] Role carregada:', userRole);
          setRole(userRole);
        }

        // Check if user is blocked
        console.log('🔍 [USER_ROLE] Verificando se usuário está bloqueado...');
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('is_blocked')
          .eq('id', user.id)
          .single();

        console.log('🔍 [USER_ROLE] Status de bloqueio:', profile?.is_blocked ? 'BLOQUEADO' : 'ATIVO');

        if (profile?.is_blocked) {
          console.warn('⚠️ [USER_ROLE] Usuário bloqueado, fazendo logout...');
          await supabase.auth.signOut();
          toast({
            title: 'Acesso bloqueado',
            description: 'Sua conta foi bloqueada. Entre em contato com o administrador.',
            variant: 'destructive',
          });
          navigate('/login');
        }
      } catch (error) {
        console.error('❌ [USER_ROLE] Erro crítico em fetchUserRole:', error);
        setRole('guest');
      } finally {
        console.log('✅ [USER_ROLE] fetchUserRole concluído');
        setLoading(false);
      }
    };

    fetchUserRole();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchUserRole();
    });

    // Subscribe to user_profiles changes for realtime blocking detection
    const profileChannel = supabase
      .channel('user_profile_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
        },
        (payload) => {
          // Check if this update is for the current user and they were blocked
          const checkBlocking = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user && payload.new.id === user.id && payload.new.is_blocked) {
              setTimeout(async () => {
                await supabase.auth.signOut();
                toast({
                  title: 'Acesso bloqueado',
                  description: 'Sua conta foi bloqueada. Entre em contato com o administrador.',
                  variant: 'destructive',
                });
                navigate('/login');
              }, 0);
            }
          };
          checkBlocking();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
      supabase.removeChannel(profileChannel);
    };
  }, [navigate, toast]);

  return { role, loading, isAdmin: role === 'admin', isEditor: role === 'editor', isCliente: role === 'cliente', isGuest: role === 'guest', isRsvp: role === 'rsvp' };
}
